import {none, Option, some} from "fp-ts/lib/Option"
import {GraphQL} from "../graphql"
import * as React from "react"
import {Button, Header, Loader} from "semantic-ui-react"
import {CUSoonStripeCheckout} from "./StripeCard"
import {pollWithTimeout} from "../timeout"
import {ID} from "../model/id"
import {
  Booker,
  Booker as BOOKER,
  CreateStripeCharge,
  ResendInvoice,
  ResendInvoiceStatus,
  StripeCharge,
  StripeChargeFragFragment,
  XeroInvoice,
} from "../graphql/queries"
import {errorUnionToEither, handleError, newError, OrError} from "../graphql/errors"
import {left, right} from "fp-ts/lib/Either"
import {flattenEither, liftOptionInEither, liftPromiseInEither} from "../tsutil"
import {ModalStore, WithModalStore} from "../App"
import {formatMoney} from "../model/charges"

interface InvoiceData {
  id: ID
  contact: {
    name: string
    email: string | null
  }
  xeroUuid: ID
  total: number
  paid: number
  owing: number
  number: string
}

interface InvoicePageProps extends WithModalStore {
  booker: Booker | null
  invoice: InvoiceData
  onToken: (token: string, amountCents: number) => Promise<() => void>
  onSendInvoice: () => Promise<void>
  resending: boolean
}

function InvoicePage({
  booker,
  invoice,
  onToken,
  onSendInvoice,
  resending,
  modalStore,
}: InvoicePageProps) {
  return (
    <div style={{textAlign: "center"}}>
      <Header>Invoice {invoice.number}</Header>
      <Header>{invoice.contact.email || invoice.contact.name}</Header>
      <Header>{formatMoney(invoice.owing)}</Header>
      {invoice.owing > 0 ? (
        <CUSoonStripeCheckout
          label="Pay {{amount}} now"
          amount={invoice.owing}
          email={invoice.contact.email || undefined}
          onToken={(token) => onToken(token.id, invoice.owing)}
          currency="AUD"
          rememberMe={true}
          modalStore={modalStore}
        >
          Pay Now
        </CUSoonStripeCheckout>
      ) : (
        <Header>PAID</Header>
      )}
      {booker && booker.isAdmin ? (
        <Header>
          <Button
            onClick={() => {
              modalStore.handlePromiseThrow(onSendInvoice())
            }}
          >
            Resend Invoice
          </Button>
        </Header>
      ) : null}
      {resending && (
        <Loader active={true} inline={true} size="small">
          Resending invoice...
        </Loader>
      )}
    </div>
  )
}

interface InvoicePageWaitingProps extends WithModalStore {
  booker: Booker | null
  invoice: InvoiceData
  onToken: (token: string, amountCents: number) => Promise<() => void>
  onSendInvoice: () => Promise<void>
}

class WaitingOnInvoiceResend extends React.Component<InvoicePageWaitingProps, {loading: boolean}> {
  constructor(props: InvoicePageWaitingProps) {
    super(props)
    this.state = {loading: false}
  }

  render() {
    return (
      <InvoicePage
        {...this.props}
        resending={this.state.loading}
        onSendInvoice={async () => {
          this.setState({loading: true})
          try {
            return await this.props.onSendInvoice()
          } finally {
            this.setState({loading: false})
          }
        }}
      />
    )
  }
}

interface InvoicePageWithDataProps extends WithModalStore {
  booker: Booker | null
  invoiceId: ID
}

function InvoicePageWithData(props: InvoicePageWithDataProps) {
  const {invoiceId} = props
  return (
    <GraphQL.Query
      query={XeroInvoice}
      fetchPolicy="cache-and-network"
      variables={{id: invoiceId}}
      render={({data, loading, refetch}) => {
        if (loading || !data) {
          return <Loader active={true} />
        }
        const {xeroInvoice} = data
        return (
          <WaitingOnInvoiceResend
            {...props}
            invoice={xeroInvoice}
            onToken={async (token, amount) => {
              await createStripeChargeWithModal(props.modalStore, xeroInvoice, token, amount)
              return () => {
                props.modalStore.handlePromiseThrow(refetch())
              }
            }}
            onSendInvoice={async () => {
              await resendInvoiceWithModal(props.modalStore, xeroInvoice)
            }}
          />
        )
      }}
    />
  )
}

async function createStripeChargeWithModal(
  modalStore: ModalStore,
  invoice: InvoiceData,
  token: string,
  amount: number,
) {
  return modalStore.applyModal({
    error: (m) => ["Payment failed", m],
    action: () => createStripeCharge(invoice, token, amount),
  })
}

async function resendInvoiceWithModal(modalStore: ModalStore, invoice: InvoiceData) {
  return modalStore.applyModal({
    error: (m) => ["Resending invoice failed", m],
    ok: () => ["Invoice sent", "Invoice sent"],
    action: () => resendInvoice(invoice.id),
  })
}

function parseChargeResult(charge: StripeChargeFragFragment): Option<OrError<void>> {
  if (charge.isFailed) {
    // Error
    return some(left(newError(charge.failedMessage || "Unknown error")))
  } else if (charge.isComplete) {
    // All good
    return some(right(undefined))
  } else  {
    // Not ready yet
    return none
  }
}

async function createStripeCharge(
  invoice: InvoiceData,
  token: string,
  amountCents: number,
): Promise<OrError<void>> {
  const data = await GraphQL.mutate(CreateStripeCharge, {
    stripeToken: token,
    invoiceId: invoice.id,
    amountCents,
  })
  const charge = data.createStripeCharge
  return parseChargeResult(charge).foldL(
    () => pollWithTimeout(async () => parseChargeResult(await fetchStripeCharge(charge.id))),
    async (x) => x,
  )
}

interface Booker {
  isAdmin: boolean
}

async function fetchStripeCharge(id: ID): Promise<StripeChargeFragFragment> {
  return (await GraphQL.query(StripeCharge, {id}, {noCache: true})).stripeCharge
}

async function resendInvoice(invoiceId: ID): Promise<OrError<void>> {
  const status = await GraphQL.mutate(ResendInvoice, {invoiceId})
  const result = errorUnionToEither(status.resendInvoice).map(
    async (status): Promise<OrError<void>> => {
      if (status.isComplete) {
        return right(undefined)
      } else {
        return pollWithTimeout(async () => {
          const status2 = await fetchSendInvoiceStatus(status.id)
          return liftOptionInEither(
            status2.map((status) => (status.isComplete ? some(undefined) : none)),
          )
        })
      }
    },
  )
  return flattenEither(await liftPromiseInEither(result))
}

interface SendInvoiceStatus {
  id: ID
  isComplete: boolean
  isFailed: boolean
}

async function fetchSendInvoiceStatus(id: ID): Promise<OrError<SendInvoiceStatus>> {
  const {resendInvoiceStatus: status} = await GraphQL.query(
    ResendInvoiceStatus,
    {id},
    {noCache: true},
  )
  return errorUnionToEither(status)
}

interface InvoicePageWithBookerProps extends WithModalStore {
  invoiceId: ID
}

function WithBooker(props: InvoicePageWithBookerProps) {
  return (
    <GraphQL.Query
      query={BOOKER}
      variables={{}}
      render={({data}) => {
        if (data) {
          return handleError(errorUnionToEither(data.booker), (data) => (
            <InvoicePageWithData {...props} booker={data.booker} />
          ))
        }
        return <div>Loading..</div>
      }}
    />
  )
}

export {WithBooker as InvoicePage}



// WEBPACK FOOTER //
// ./src/components/InvoicePage.tsx